import { AxiosResponse } from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import {
    getAppStoreDashboard,
    getAppStoreIssue,
    runAppStoreCheck,
    sendAppStoreAlert,
    getAppleSetupQueue,
    completeAppleSetup as completeAppleSetupRequest,
    sendAppleNeedsCorrection,
    retrieveAppleKey as retrieveAppleKeyRequest,
} from "../../services/app-store-monitor/app-store-monitor.service";
import {
    getDashboard,
    getDashboardFailure,
    getDashboardSuccess,
    getIssue,
    getIssueFailure,
    getIssueSuccess,
    runCheck,
    runCheckFailure,
    runCheckSuccess,
    sendAlert,
    sendAlertFailure,
    sendAlertSuccess,
    getAppleQueue,
    getAppleQueueSuccess,
    getAppleQueueFailure,
    completeApple,
    completeAppleSuccess,
    completeAppleFailure,
    needsCorrectionApple,
    needsCorrectionAppleSuccess,
    needsCorrectionAppleFailure,
    retrieveAppleKey,
    retrieveAppleKeySuccess,
    retrieveAppleKeyFailure,
} from "../reducer/app-store-monitor";
import {
    GetDashboardActionPayload,
    GetIssueActionPayload,
    SendAlertActionPayload,
    CompleteApplePayload,
    NeedsCorrectionApplePayload,
    RetrieveAppleKeyPayload,
} from "../types/app-store-monitor";
import {
    IAppleSetupQueueItem,
    IAppleSetupQueueResource,
} from "../types/apple-setup";

function* getDashboardSaga(action: GetDashboardActionPayload) {
    try {
        const { data }: AxiosResponse = yield call(
            getAppStoreDashboard,
            action.payload.filter
        );
        yield put(getDashboardSuccess(data.attributes));
    } catch (error: any) {
        const status = error?.response?.status;
        yield put(
            getDashboardFailure({
                forbidden: status === 403,
            })
        );
    }
}

function* getIssueSaga(action: GetIssueActionPayload) {
    try {
        const { data }: AxiosResponse = yield call(getAppStoreIssue, action.payload);
        yield put(
            getIssueSuccess({ id: action.payload, attributes: data.attributes })
        );
    } catch (_error: any) {
        yield put(getIssueFailure({ id: action.payload }));
    }
}

function* runCheckSaga() {
    try {
        const { data }: AxiosResponse = yield call(runAppStoreCheck);
        yield put(runCheckSuccess(data.attributes));
    } catch (_error: unknown) {
        yield put(runCheckFailure());
    }
}

function* sendAlertSaga(action: SendAlertActionPayload) {
    try {
        const { data }: AxiosResponse = yield call(sendAppStoreAlert, action.payload);
        yield put(
            sendAlertSuccess({ id: action.payload, attributes: data.attributes })
        );
    } catch (_error: any) {
        yield put(sendAlertFailure());
    }
}

function* getAppleQueueSaga() {
    try {
        // The response interceptor unwraps `response.data`, so `data` here is
        // already the response body's JSON:API resource array.
        const { data }: AxiosResponse = yield call(getAppleSetupQueue);
        const items: IAppleSetupQueueItem[] = (
            data as IAppleSetupQueueResource[]
        ).map((resource) => ({
            id: Number(resource.id),
            ...resource.attributes,
        }));
        yield put(getAppleQueueSuccess(items));
    } catch (error: any) {
        yield put(getAppleQueueFailure({ forbidden: error?.response?.status === 403 }));
    }
}

function* completeAppleSaga(action: { type: string; payload: CompleteApplePayload }) {
    try {
        yield call(completeAppleSetupRequest, action.payload.orgId);
        yield put(completeAppleSuccess());
        yield put(getAppleQueue());
    } catch (_error: unknown) {
        yield put(completeAppleFailure());
    }
}

function* needsCorrectionAppleSaga(action: { type: string; payload: NeedsCorrectionApplePayload }) {
    try {
        yield call(sendAppleNeedsCorrection, action.payload.orgId, action.payload.note);
        yield put(needsCorrectionAppleSuccess());
        yield put(getAppleQueue());
    } catch (_error: unknown) {
        yield put(needsCorrectionAppleFailure());
    }
}

function* retrieveAppleKeySaga(action: { type: string; payload: RetrieveAppleKeyPayload }) {
    try {
        const text: string = yield call(retrieveAppleKeyRequest, action.payload.orgId, action.payload.reason);
        const blob = new Blob([text], { type: "application/x-pem-file" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${action.payload.tenantName}-AuthKey.p8`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        yield put(retrieveAppleKeySuccess());
    } catch (_error: unknown) {
        yield put(retrieveAppleKeyFailure());
    }
}

export function* rootSaga() {
    yield takeLatest(getDashboard.type, getDashboardSaga);
    yield takeLatest(getIssue.type, getIssueSaga);
    yield takeLatest(runCheck.type, runCheckSaga);
    yield takeLatest(sendAlert.type, sendAlertSaga);
    yield takeLatest(getAppleQueue.type, getAppleQueueSaga);
    yield takeLatest(completeApple.type, completeAppleSaga);
    yield takeLatest(needsCorrectionApple.type, needsCorrectionAppleSaga);
    yield takeLatest(retrieveAppleKey.type, retrieveAppleKeySaga);
}
